import { AxiosInstance, AxiosPromise, AxiosResponse } from "axios";
import { ComplyCubeApiError } from "./errors/ComplyCubeApiError";
import { ComplyCubeError } from "./errors/ComplyCubeError";

type SimpleObject = { [key: string]: unknown };

export enum Method {
  GET = "get",
  POST = "post",
  DELETE = "delete"
}

const convertAxiosErrorToComplyCubeError = async (
  error: any
): Promise<ComplyCubeError> => {
  if (!error.response) {
    return new ComplyCubeError(
      error.message || "An unknown error occurred making the request"
    );
  }

  // Received a 4XX or 5XX response.
  const response: AxiosResponse = error.response;
  const data = response.data;
  return ComplyCubeApiError.fromResponse(data, response.status);
};

const handleResponse = async (request: AxiosPromise<any>): Promise<any> => {
  try {
    const response = await request;
    const data = response.data;
    return data;
  } catch (error) {
    throw await convertAxiosErrorToComplyCubeError(error);
  }
};

export class Resource<T extends SimpleObject> {
  public name: string;
  private readonly axiosInstance: AxiosInstance;

  protected constructor(name: string, axiosInstance: AxiosInstance) {
    this.name = name;
    this.axiosInstance = axiosInstance;
  }

  protected async request({
    method,
    path = "",
    body,
    query
  }: {
    method: Method;
    path?: string;
    body?: T;
    query?: SimpleObject;
  }): Promise<any> {
    const request = this.axiosInstance({
      method,
      url: `${this.name}/${path}`,
      data: body,
      params: query
    });

    return handleResponse(request);
  }

  protected async upload(
    path: string,
    body: any,
    documentSide?: string | null
  ): Promise<any> {
    const finalPath = documentSide ? `${path}/upload/${documentSide}` : path;
    const request = this.axiosInstance({
      method: Method.POST,
      url: `${this.name}/${finalPath}`,
      data: body
    });

    return handleResponse(request);
  }

  protected async download(
    path: string,
    documentSide?: string | null
  ): Promise<any> {
    const finalPath = documentSide ? `${path}/download/${documentSide}` : path;
    const request = this.axiosInstance({
      method: Method.GET,
      url: `${this.name}/${finalPath}`
    });

    return handleResponse(request);
  }

  protected async validateCheck(
    path: string,
    validation?: SimpleObject
  ): Promise<any> {
    const request = this.axiosInstance({
      method: Method.POST,
      url: `${this.name}/${path}`,
      data: validation
    });

    return handleResponse(request);
  }

  protected async completeSession(path: string): Promise<any> {
    const request = this.axiosInstance({
      method: Method.POST,
      url: `${this.name}/${path}`
    });

    return handleResponse(request);
  }
}
